import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { auth, memberID, memberName, memberOrgName, updateUserInfo } from "../../reducers/userSlice";
import { getLocalStorageUser } from "../../helper/LocalStorageHelper";
import TanitaSection from "../../sections/TanitaSection";
import CyberPanel from "../../components/common/CyberPanel";
import MemberInfoBar from "../../components/member/MemberInfoBar";
import MemberPanelActions from "../../components/member/MemberPanelActions";
import MemberSkillStatPanel from "../../components/member/MemberSkillStatPanel";
import MemberMonthlyPlayTimePanel from "../../components/member/MemberMonthlyPlayTimePanel";
import SkillpassportQRLoginDialog from "../../components/dashboard/SkillpassportQRLoginDialog";
import MemberAcademicYearSelect from "../../components/member/MemberAcademicYearSelect";
import { getDefaultAcademicYear } from "../../helper/AcademicYearHelper";
import { leaderboardBackgroundSx, cyberColors } from "../../styles/cyberTheme";

export default function MemberPanelView() {
    const dispatch = useDispatch();
    const [hydrated, setHydrated] = useState(false);
    const [qrLoginOpen, setQrLoginOpen] = useState(false);
    const [selectedYear, setSelectedYear] = useState(getDefaultAcademicYear);
    const isAuthed = useSelector(auth);
    const id = useSelector(memberID);
    const name = useSelector(memberName);
    const orgName = useSelector(memberOrgName);

    useEffect(() => {
        const stored = getLocalStorageUser();
        if (stored) {
            try {
                dispatch(updateUserInfo(JSON.parse(stored)));
            } catch (e) {
                // ignore invalid stored user
            }
        }
        setHydrated(true);
    }, [dispatch]);

    if (!hydrated) {
        return null;
    }

    if (!isAuthed || !id) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <Box sx={{ background: '#000000', height: '100vh', width: '100%' }}>
            <SkillpassportQRLoginDialog
                open={qrLoginOpen}
                onClose={() => setQrLoginOpen(false)}
                skipNavigateOnConfirm
            />
        <Box
            sx={{
                ...leaderboardBackgroundSx,
                alignItems: "stretch",
                background: '#000000',
                justifyContent: "flex-start",
                p: 2,
                gap: 1.5,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    px: 1,
                    py: 0.5,
                    borderBottom: `1px solid ${cyberColors.purple.borderMedium}`,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <MemberInfoBar name={name} orgName={orgName} />
                </Box>
                <Box sx={{ flex: "0 0 auto", display: "flex", justifyContent: "center", px: 2 }}>
                    <MemberAcademicYearSelect
                        value={selectedYear}
                        onChange={setSelectedYear}
                    />
                </Box>
                <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                    <MemberPanelActions onSwitchLogin={() => setQrLoginOpen(true)} />
                </Box>
            </Box>

            <Grid container spacing={1.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <Grid item xs={12} md={5} sx={{ height: "100%", minHeight: 0 }}>
                    <CyberPanel title="健康分析報告" sx={{ height: "100%" }}>
                        <TanitaSection
                            key={`tanita-${id}-${selectedYear}`}
                            memberID={id}
                            year={String(selectedYear)}
                            theme="cyberTheme"
                            autoLoad
                        />
                    </CyberPanel>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={7}
                    sx={{
                        height: "100%",
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <CyberPanel
                        title="運動參與數據"
                        sx={{ flex: 1, minHeight: 0 }}
                        contentSx={{ p: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
                    >
                        <Grid container spacing={1} sx={{ flex: 1, minHeight: 0 }}>
                            <Grid item xs={12} lg={7} sx={{ height: "100%", minHeight: 200 }}>
                                <MemberSkillStatPanel
                                    key={`skill-${id}-${selectedYear}`}
                                    memberID={id}
                                    year={String(selectedYear)}
                                />
                            </Grid>
                            <Grid item xs={12} lg={5} sx={{ height: "100%", minHeight: 200 }}>
                                <MemberMonthlyPlayTimePanel
                                    key={`monthly-${id}-${selectedYear}`}
                                    memberID={id}
                                    year={String(selectedYear)}
                                />
                            </Grid>
                        </Grid>
                    </CyberPanel>
                </Grid>
            </Grid>
        </Box>
        </Box>
    );
}
